import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../environments/environment';
import { RolesApiService } from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';

export interface FooterLink {
  label: string;
  url: string;
  external?: boolean;
}

export interface SocialLink {
  label: string;
  url: string;
  iconSrc: string;
}

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './footer.component.html',
  styleUrl: './footer.component.scss',
})
export class FooterComponent {
  private rolesService = inject(RolesApiService);
  private toasterService = inject(ToasterService);
  private router = inject(Router);
  private keycloak = inject(KeycloakService);

  currentYear = new Date().getFullYear();
  readonly contactEmail = 'bodh@tanuh.ai';
  readonly contactEmailHref = 'mailto:bodh@tanuh.ai';
  readonly phonePrimary = '(080) 2293 4106';
  readonly phoneSecondary = '(080) 2293 4107';

  importantLinks: FooterLink[] = [
    { label: 'Dashboard', url: '', external: false },
    { label: 'Discussions', url: '/discussions', external: false },
    { label: 'Challenges', url: '/challenges', external: false },
  ];

  developerLinks: FooterLink[] = [
    { label: 'Control Plane API docs', url: environment.controlPlaneApiDocsUrl, external: true },
    { label: 'File Server API docs', url: environment.fileServerApiDocsUrl, external: true },
    { label: 'Resource Server API docs', url: environment.resourceServerApiDocsUrl, external: true },
    // { label: 'OGC Server API docs', url: environment.ogcServerApiDocsUrl, external: true },
    // { label: 'Sandbox Server API docs', url: environment.sandboxServerApiDocsUrl, external: true }
  ];

  // Icon paths from public folder
  emailIcon = '/sms.svg';

  /** TANUH official LinkedIn */
  socialLinks: SocialLink[] = [
    {
      label: 'LinkedIn',
      url: 'https://www.linkedin.com/company/tanuh-aicoe/',
      iconSrc: '/linkedin_symbol.svg.svg',
    },
  ];

  /**
   * Check if user is logged in
   */
  private async checkLoginStatus(): Promise<boolean> {
    try {
      return await this.keycloak.isLoggedIn();
    } catch (error) {
      console.error('Error checking login status:', error);
      return false;
    }
  }

  /**
   * Handle click on Dashboard: navigate to the panel for the user's highest role.
   * Requires login; fetches roles and navigates to /admin/home, /org-admin/home, /provider/home, or /consumer/home.
   */
  async onDashboardClick(event: Event): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    try {
      const isLoggedIn = await this.checkLoginStatus();
      if (!isLoggedIn) {
        this.toasterService.warning("You must be logged in to access the dashboard.");
        return;
      }

      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();
      const route = this.rolesService.getHighestRolePanelRoute(userRoles || []);
      this.router.navigate([route]);
    } catch (error) {
      console.error('Error navigating to dashboard:', error);
      this.toasterService.warning("Unable to open dashboard. Please try again.");
    }
  }

  /**
   * Handle click on protected panel links (e.g. Canvas Playground)
   * Check if user is logged in and show toaster message if not
   */
  async onProtectedPanelClick(event: Event, linkLabel: string): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    try {
      const isLoggedIn = await this.checkLoginStatus();
      if (!isLoggedIn) {
        this.toasterService.warning("You must be logged in, in order to access the panels");
        return;
      }

      const link = this.importantLinks.find(l => l.label === linkLabel);
      if (link?.url) {
        this.router.navigate([link.url]);
      }
    } catch (error) {
      console.error('Error checking user authentication:', error);
      const link = this.importantLinks.find(l => l.label === linkLabel);
      if (link?.url) {
        this.router.navigate([link.url]);
      }
    }
  }

}


